import {
  addCategory,
  addPOI,
  removePOI,
  setCategories,
  setLoading,
  setPOIs,
  updatePOI as updatePOIInState,
} from "@/features/survey/poisSlice";
import type {
  SurveyPOI,
  SurveyPOICategory,
  SurveyPOICategoryRequest,
  SurveyPOIRequest,
} from "@survey-types/survey.poi.types";
import { surveyApi } from "./base";

/**
 * Survey POI API — CRUD for Points of Interest and their categories
 * Endpoints: GET/POST /poi/{survey_id}, GET/PATCH/DELETE /poi/{survey_id}/{poi_id}
 *            GET/POST /poi/{survey_id}/poiCategories, PATCH/DELETE /poi/{survey_id}/poiCategories/{category_id}
 */
export const surveyPOIApi = surveyApi
  .enhanceEndpoints({ addTagTypes: ["POI", "POICategories"] })
  .injectEndpoints({
    endpoints: (build) => ({
      /**
       * Fetch all POIs for a survey
       */
      getPOIs: build.query<SurveyPOI[], string>({
        query: (surveyId) => ({ url: `/poi/${surveyId}`, method: "GET" }),
        // Prevent automatic refetches to avoid flickering when optimistic updates are applied
        // POIs will only refetch on:
        // - Initial page load
        // - Explicit cache invalidation (errors)
        // - Manual refetch calls
        providesTags: (_result, _error, surveyId) => [
          { type: "POI", id: surveyId },
        ],
        async onQueryStarted(_surveyId, { dispatch, queryFulfilled }) {
          dispatch(setLoading(true));
          try {
            const { data } = await queryFulfilled;
            dispatch(setPOIs(data));
          } catch {
            dispatch(setPOIs([]));
          } finally {
            dispatch(setLoading(false));
          }
        },
      }),

      /**
       * Fetch all POI categories for a survey
       */
      getPOICategories: build.query<SurveyPOICategory[], string>({
        query: (surveyId) => ({
          url: `/poi/${surveyId}/poiCategories`,
          method: "GET",
        }),
        // Categories rarely change, no need to refetch on component mount
        providesTags: (_result, _error, surveyId) => [
          { type: "POICategories", id: surveyId },
        ],
        async onQueryStarted(_surveyId, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(setCategories(data));
          } catch {
            dispatch(setCategories([]));
          }
        },
      }),

      /**
       * Create a new POI for a survey
       */
      createPOI: build.mutation<
        SurveyPOI,
        { surveyId: string; body: SurveyPOIRequest }
      >({
        query: ({ surveyId, body }) => ({
          url: `/poi/${surveyId}`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "POI", id: surveyId },
        ],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(addPOI(data));
          } catch {
            // RTK Query handles the error state
          }
        },
      }),

      /**
       * Update an existing POI
       */
      updatePOI: build.mutation<
        SurveyPOI,
        { surveyId: string; poiId: string; body: Partial<SurveyPOIRequest> }
      >({
        query: ({ surveyId, poiId, body }) => ({
          url: `/poi/${surveyId}/${poiId}`,
          method: "PATCH",
          body,
        }),
        // Don't invalidate cache - optimistic update keeps state in sync
        // Refetch only happens on navigation or explicit refresh
        // This prevents the flickering issue caused by unnecessary refetches
        invalidatesTags: () => [],
        async onQueryStarted(
          { surveyId, poiId, body },
          { dispatch, queryFulfilled },
        ) {
          // Optimistic update: immediately update the Redux state
          dispatch(
            updatePOIInState({
              extId: poiId,
              updates: body,
            }),
          );

          try {
            await queryFulfilled;

            // Server response received successfully
            // No need to update again - optimistic update was sufficient
            // (If server response differs significantly, it will be caught on next fetch)
          } catch (_error) {
            // On error, invalidate the POI cache so the query refetches
            // through the normal RTK Query invalidation flow.
            console.error("Failed to update POI");
            dispatch(
              surveyPOIApi.util.invalidateTags([{ type: "POI", id: surveyId }]),
            );
          }
        },
      }),

      /**
       * Delete a POI
       */
      deletePOI: build.mutation<void, { surveyId: string; poiId: string }>({
        query: ({ surveyId, poiId }) => ({
          url: `/poi/${surveyId}/${poiId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "POI", id: surveyId },
        ],
        async onQueryStarted(
          { poiId },
          { dispatch, queryFulfilled },
        ) {
          try {
            await queryFulfilled;
            dispatch(removePOI(poiId));
          } catch {
            // RTK Query handles the error state
          }
        },
      }),

      /**
       * Reorder a POI within a survey
       */
      reorderPOI: build.mutation<
        void,
        { surveyId: string; poiId: string; order: number }
      >({
        query: ({ surveyId, poiId, order }) => ({
          url: `/poi/${surveyId}/reorder`,
          method: "PATCH",
          body: { surveyId, poiId, order },
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "POI", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // RTK Query handles the error state
          }
        },
      }),

      /**
       * Fetch a single POI category
       */
      getPOICategory: build.query<
        SurveyPOICategory,
        { surveyId: string; categoryId: string }
      >({
        query: ({ surveyId, categoryId }) => ({
          url: `/poi/${surveyId}/poiCategories/${categoryId}`,
          method: "GET",
        }),
        providesTags: (_result, _error, { surveyId, categoryId }) => [
          { type: "POICategories", id: `${surveyId}-${categoryId}` },
        ],
      }),

      /**
       * Create a new POI category
       */
      createPOICategory: build.mutation<
        SurveyPOICategory,
        { surveyId: string; body: SurveyPOICategoryRequest }
      >({
        query: ({ surveyId, body }) => ({
          url: `/poi/${surveyId}/poiCategories`,
          method: "POST",
          body,
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data: createdCategory } = await queryFulfilled;
            dispatch(addCategory(createdCategory));
          } catch (_error) {
            // Handle error if needed
            console.error("Error in createPOICategory:");
          }
        },
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "POICategories", id: surveyId },
        ],
      }),

      /**
       * Update a POI category
       *
       * We keep the optimistic update for immediate feedback, but invalidate the
       * categories cache on success so any server-computed fields are refreshed.
       * This prevents the UI from staying stale if the server returns values that
       * differ from the optimistic payload.
       */
      updatePOICategory: build.mutation<
        SurveyPOICategory,
        {
          surveyId: string;
          categoryId: string;
          body: Partial<SurveyPOICategoryRequest>;
        }
      >({
        query: ({ surveyId, categoryId, body }) => ({
          url: `/poi/${surveyId}/poiCategories/${categoryId}`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "POICategories", id: surveyId },
        ],
        async onQueryStarted({ surveyId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (error) {
            // On error, invalidate the POI categories cache so the query
            // refetches through the normal RTK Query invalidation flow.
            console.error("Failed to update POI category:", error);
            dispatch(
              surveyPOIApi.util.invalidateTags([
                { type: "POICategories", id: surveyId },
              ]),
            );
          }
        },
      }),

      /**
       * Delete a POI category
       */
      deletePOICategory: build.mutation<
        void,
        { surveyId: string; categoryId: string }
      >({
        query: ({ surveyId, categoryId }) => ({
          url: `/poi/${surveyId}/poiCategories/${categoryId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "POICategories", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // RTK Query handles the error state
          }
        },
      }),

      /**
       * Step 1: Upload POIs Excel file — returns matched/unmatched columns
       */
      importPOIsFromExcel: build.mutation<
        {
          matched: string[];
          notMatched: string[];
          unParsedColumns: string[][];
        },
        { file: File; surveyId: string }
      >({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `/import/poi`,
            method: "POST",
            body: formData,
          };
        },
      }),

      /**
       * Step 2: Parse POIs with user-confirmed column mapping
       */
      parsePOIs: build.mutation<
        any,
        {
          unParsedColumns: string[][];
          headerMapping: Array<{ header: string; matchedAttribute: string }>;
        }
      >({
        query: ({ unParsedColumns, headerMapping }) => ({
          url: `/import/parsepoi`,
          method: "POST",
          body: { unParsedColumns, headerMapping },
        }),
      }),

      /**
       * Step 3: Bulk-import parsed POIs into the survey
       */
      bulkImportPOIs: build.mutation<
        { importedPOIs: SurveyPOI[]; count: number },
        {
          surveyId: string;
          pois: Array<Partial<SurveyPOIRequest> & { categoryName?: string }>;
        }
      >({
        query: ({ surveyId, pois }) => ({
          url: `/poi/${surveyId}/bulkImport`,
          method: "POST",
          body: { surveyId, pois },
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "POI", id: surveyId },
          { type: "POICategories", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // RTK Query handles the error state
          }
        },
      }),
    }),
  });

export const {
  useGetPOIsQuery,
  useGetPOICategoriesQuery,
  useGetPOICategoryQuery,
  useCreatePOIMutation,
  useUpdatePOIMutation,
  useDeletePOIMutation,
  useReorderPOIMutation,
  useCreatePOICategoryMutation,
  useUpdatePOICategoryMutation,
  useDeletePOICategoryMutation,
  useImportPOIsFromExcelMutation,
  useParsePOIsMutation,
  useBulkImportPOIsMutation,
} = surveyPOIApi;
